import { DateTime, Duration } from 'luxon';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil, tap } from 'rxjs/operators';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DurationTimeSliderComponent } from '../duration-time-slider/duration-time-slider.component';
import { NgIf } from '@angular/common';
import { DurationInputComponent } from '../duration-input/duration-input.component';
import { AppErrorMessagesComponent } from '../../error-messages/app-error-messages.component';
import { MaskedDateTimeService, TimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { TimePickerWithTimeSliderFormGroup } from './time-picker-with-time-slider-form.type';

export interface IDurationRange {
  startTime: Duration;
  endTime: Duration;
}

@Component({
    selector: 'app-time-picker-with-time-slider',
    templateUrl: './time-picker-with-time-slider.component.html',
    styleUrls: ['./time-picker-with-time-slider.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, TimePickerModule, AppErrorMessagesComponent, DurationInputComponent, NgIf, DurationTimeSliderComponent, TranslateModule],
    providers: [MaskedDateTimeService]
})
export class TimePickerWithTimeSliderComponent implements OnInit, OnDestroy {

  @Input() public form: TimePickerWithTimeSliderFormGroup;
  @Input() public maxDuration: number;
  @Input() public possibleStartTimeRanges: IDurationRange[];
  @Input() public timepickerStrictMode = false;
  @Input() public showSliderBeforeTimeSelection = true;
  @Output() public startTimeChange = new EventEmitter();

  public customFormat = 'HH:mm';
  public endTimeStr: string;
  public showSlider = true;

  public get maxDate(): Date {
    if (this.maxDuration && this.possibleStartTimeRanges?.length > 0) {
      const targetMaxDate = DateTime.fromISO('1970-01-01').plus(this.possibleStartTimeRanges[0].startTime).plus({minutes: this.maxDuration});
      if (targetMaxDate.day > 1) { // timepicker can only display max 23:59
        return new Date('1/1/1970 23:59');
      }

      return targetMaxDate.toJSDate();
    }
    return new Date('1/1/1970 23:59');
  }

  private unsubscribe$ = new Subject<void>();

  constructor() { }

  ngOnInit(): void {
    combineLatest([
      this.form.controls.startTime.valueChanges.pipe(
        startWith(this.form.controls.startTime.value),
        tap(val => {
          if (!this.showSliderBeforeTimeSelection) {
            this.showSlider = !!val;
          }
        })
      ),
      this.form.controls.duration.valueChanges.pipe(startWith(this.form.controls.duration.value)),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
      )
      .subscribe(([startTime, duration]) => {
        this.startTimeChange.emit(startTime);
        this.endTimeStr = null;
        if (startTime !== null && duration !== null) {
          startTime = new Date(startTime);
          startTime.setFullYear(1970, 0, 1);

          if (startTime <= this.maxDate) {
            this.endTimeStr = this.calculatedEndTime(startTime, duration);
          }
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public durationChange(duration: number) {
    this.form.controls.duration.setValue(duration);
  }

  private calculatedEndTime(startTime: Date, duration: number): string {
    if (startTime && duration) {
      return DateTime.fromJSDate(startTime).plus({minutes: duration}).toFormat('HH:mm');
    }
    return null;
  }
}
